import styles from "./client-profile-alerts-tab.module.css";

import classNames from "classnames";
import { ClientProfileSettings, ClientProfileDetails } from "../../store/types";
import { inject, observer } from "mobx-react";
import TimeAlertRadio from "../../cells/time-alerts-radio";
import ClientAlertStatusIcons from "../../cells/client-status-group";
import { Edit } from "../../../../components/svg";
import { AuthStore, TransitionStore } from "../../../../store";
import { useTranslation } from "react-i18next";
import { CreateConfirmationDialog } from "../../../../utils/ConfirmationDialog";
import { ChangeEvent } from "react";
import { SwitchInput } from "../../../../components/form-inputs";
import { useNavigate } from "react-router-dom";
import { ClientProfileStore } from "../../store/store";

interface IClientProfileAlertsTabProps {
  clientDetails: ClientProfileDetails;
  clientSettings: ClientProfileSettings;
  transitionStore: TransitionStore;
  authStore: AuthStore;
  clientProfileStore?: ClientProfileStore;
  toggleAlertStatus(): void;
  switchAlertTimeStatus(): void;
}

function ClientProfileAlertsTab(props: IClientProfileAlertsTabProps) {
  const { t } = useTranslation("client-profile");
  const {
    clientSettings,
    clientDetails,
    toggleAlertStatus,
    authStore,
    transitionStore,
    switchAlertTimeStatus,
    clientProfileStore,
  } = props;
  const { isAlarmProfileEnabled, activeAlertPeriodId } = clientDetails;

  const history = useNavigate();

  const render = () => {
    return (
      <div className={styles["tabs-container"]}>
        <li className={`${styles["item"]} ${styles["odd-tab"]}`}>
          <div className={styles["title"]}>
            {t("Alerts triggered from the room")}
          </div>
        </li>
        <li className={`${styles["item"]} ${styles["even-tab"]}`}>
          {renderAlertsToggleButton()}
          {renderTimeIntervalSwitches()}
          {renderAlertsStatusIcons()}
          {renderEditAlertSettingsButtons()}
        </li>
      </div>
    );
  };

  const renderAlertsToggleButton = () => {
    const { dialogTitle, dialogContent, okButtonText, cancelButtonText } =
      prepareToggleDialogInformation();

    const confirmationDialogData = CreateConfirmationDialog(
      dialogTitle,
      dialogContent,
      okButtonText,
      cancelButtonText,
      false,
      () => {
        clientDetails.isAlarmProfileEnabled =
          !clientDetails.isAlarmProfileEnabled;
        toggleAlertStatus();
      }
    );

    const emptySpaces = [...Array(2)].map((a) => {
      return <div className={styles["row-2"]} />;
    });

    return (
      <div className={`${styles["toggle-button"]} ${styles["col-1"]}`}>
        <div className={styles["row-1"]}>
          <div className={styles["switch-container"]}>
            <SwitchInput
              checked={clientDetails.isAlarmProfileEnabled}
              disabled={!authStore.hasAdminRights()}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                transitionStore.showGlobalDialog(confirmationDialogData);
              }}
            />
          </div>
        </div>
        {emptySpaces}
      </div>
    );
  };

  const prepareToggleDialogInformation = () => {
    const toggleStatus = !clientDetails.isAlarmProfileEnabled
      ? t("common:On")
      : t("common:Off");
    const action = !clientDetails.isAlarmProfileEnabled
      ? t("common:Start")
      : t("common:Stop");

    const dialogTitle = t("AlertsToggleDialogTitle", {
      toggleStatus: toggleStatus,
    });
    const okButtonText = t("AlertsToggleDialogConfirmation", {
      toggleStatus: toggleStatus,
    });
    const cancelButtonText = t("common:Cancel");

    const dialogContent = `${t("AlertsToggleDialogAction", { action: action })}
    ${t("AlertsToggleDialogQuestion", {
      toggleStatus: toggleStatus,
      clientName: clientDetails.clientName,
    })}`;

    return { dialogTitle, dialogContent, okButtonText, cancelButtonText };
  };

  const renderTimeIntervalSwitches = () => {
    const dayInterval = clientSettings.getDaytimeInterval();
    const nightInterval = clientSettings.getNighttimeInterval();

    const intervalsList = clientProfileStore?.clientProfileSettings.intervals;

    const intervalTranslationKey: { [index: string]: string } = {
      Daytime: "Day time alerts",
      Nighttime: "Night time alerts",
      None: "Custom alerts",
    };

    const intervalStartTimes: {
      [index: string]: { start: string | null; end: string | null };
    } = {
      Daytime: {
        start: formatHour(dayInterval.startTime),
        end: formatHour(nightInterval.startTime),
      },
      Nighttime: {
        start: formatHour(nightInterval.startTime),
        end: formatHour(dayInterval.startTime),
      },
      None: {
        start: null,
        end: null,
      },
    };

    const intervalItems = intervalsList?.map((interval) => {
      const { dialogTitle, dialogContent, okButtonText, cancelButtonText } =
        prepareSwitchDialogInformation(interval.intervalType);

      const dialogData = CreateConfirmationDialog(
        dialogTitle,
        dialogContent,
        okButtonText,
        cancelButtonText,
        false,
        () => {
          changeAlertStatus(interval.intervalId, interval.intervalType);
        }
      );

      return (
        <div
          className={classNames({
            [styles["row-1"]]: isAlertTabEnabled(),
            [`${styles["row-1"]} ${styles["disabled"]}`]: !isAlertTabEnabled(),
          })}
        >
          <TimeAlertRadio
            title={t(intervalTranslationKey[interval.intervalType])}
            checked={isPeriodActive(interval.intervalId)}
            disabledInteraction={isPeriodActive(interval.intervalId)}
            onChange={(event: { target: { value: any } }) => {
              transitionStore.showGlobalDialog(dialogData);
            }}
            startTime={intervalStartTimes[interval.intervalType].start}
            endTime={intervalStartTimes[interval.intervalType].end}
          />
        </div>
      );
    });

    return (
      <div className={`${styles["text"]} ${styles["col-2"]}`}>
        {intervalItems}
      </div>
    );
  };

  const prepareSwitchDialogInformation = (type: string) => {
    const dayInterval = clientSettings.getDaytimeInterval();
    const nightInterval = clientSettings.getNighttimeInterval();
    const { clientDetails } = props;

    const activePeriodName: { [index: string]: string } = {
      Daytime: t("Day"),
      Nighttime: t("Night"),
      None: `${t("Custom")} `,
    };

    const nextPeriodName: { [index: string]: string } = {
      Daytime: t("Night"),
      Nighttime: t("Day"),
      None: `${t("Custom")} `,
    };

    const endTimePeriod: { [index: string]: string | null } = {
      Daytime: nightInterval.startTime,
      Nighttime: dayInterval.startTime,
      None: null,
    };

    let dialogContent = "";

    if (type === "None") {
      dialogContent = `${t("AlertsSwitchDialogActionCustom", {
        clientName: clientDetails.clientName,
      })}`;
    } else {
      dialogContent = `${t("AlertsSwitchDialogAction", {
        selectedPeriod: activePeriodName[type],
        endHour: endTimePeriod[type]
          ? formatHour(endTimePeriod[type]!)
          : "none",
        nextPeriod: nextPeriodName[type],
      })} 
    
      ${t("AlertsSwitchDialogQuestion", {
        selectedPeriod: activePeriodName[type],
        clientName: clientDetails.clientName,
      })}`;
    }

    const dialogTitle = t("AlertsSwitchDialogTitle", {
      selectedPeriod: activePeriodName[type],
    });
    const okButtonText = t("AlertsSwitchDialogConfirmation", {
      selectedPeriod: activePeriodName[type],
    });
    const cancelButtonText = t("common:Cancel");

    return { dialogTitle, dialogContent, okButtonText, cancelButtonText };
  };

  const renderAlertsStatusIcons = () => {
    const intervalsList = clientProfileStore?.clientProfileSettings.intervals;

    const intervalItems = intervalsList?.map((interval) => {
      return (
        <div className={styles["row-1"]}>
          <ClientAlertStatusIcons
            clientSettings={clientSettings}
            disabled={
              !isPeriodActive(interval.intervalId) ||
              !clientDetails.isAlarmProfileEnabled
            }
            alertPeriodId={interval.intervalId}
          />
        </div>
      );
    });

    return <div className={styles["col-3"]}> {intervalItems}</div>;
  };

  const renderEditAlertSettingsButtons = () => {
    const intervalsList = clientProfileStore?.clientProfileSettings.intervals;

    const intervalTranslationKey: { [index: string]: string } = {
      Daytime: "Edit day time alerts",
      Nighttime: "Edit night time alerts",
      None: "Edit custom alerts",
    };

    const intervalItems = intervalsList?.map((interval) => {
      return (
        <>
          <div className={`${styles["row-2"]} ${styles["edit-text"]}`}>
            <Edit className={styles["edit-day-time-icon"]} />
            <div
              className={styles["edit-day-time-text"]}
              onClick={() => {
                history(
                  "/client-profile/alert-settings/" + clientDetails.clientId,
                  { state: { editingPeriodId: interval.intervalId } }
                );
              }}
            >
              {t(intervalTranslationKey[interval.intervalType])}
            </div>
          </div>
        </>
      );
    });

    return (
      <div className={styles["col-4"]}>
        {authStore.hasAdminRights() && intervalItems}
      </div>
    );
  };

  const changeAlertStatus = (value: number, type: string) => {
    clientDetails.activeAlertPeriodId = value;
    clientDetails.activeAlertPeriodType = type;
    switchAlertTimeStatus();
  };

  const isAlertTabEnabled = () => {
    return isAlarmProfileEnabled;
  };

  const isPeriodActive = (periodId: number) => {
    return activeAlertPeriodId === periodId;
  };

  const formatHour = (hour: string) => {
    return hour.length > 5 ? hour.substring(0, 5) : hour;
  };

  return render();
}

export default inject("clientProfileStore")(observer(ClientProfileAlertsTab));
